import { MarkdownContent } from '@backstage/core-components';
import {
  appThemeApiRef,
  featureFlagsApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Chip,
  Link,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core';
import ReactMarkdown from 'react-markdown';
import { PdsAccordion, PdsHeading } from '@principal/design-system-react';
import { Fragment, ReactElement, useState } from 'react';
import { ErrorOutline } from '@mui/icons-material';
import { prefersDarkMode } from '@principal-developer-portal/ui';
import { DueDate } from '../../data-table/utils/types';
import { YardstickAutoFix } from '@internal/yardstick-common';
import { AutoRemediationBadge } from '../../data-table/auto-remediation-badge';
import { AutoRemediationButton } from './auto-remediation-button';
import { AutofixV2LogicalAccordionComponent } from './autofix-v2-logical-accordion-component';
import { shouldShowRemediation } from '../../../feature-flags/sca-remediation';

// eslint-disable-next-line max-lines-per-function
const useStyles = makeStyles(theme => ({
  measurementsCard: {
    // @ts-expect-error custom color
    backgroundColor: theme.palette.search.background,
    height: '100%',
  },
  resultsCard: {
    height: '100%',
  },
  selectedMeasurement: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.dark}`,
    borderRadius: '4px',
    padding: '.5rem 1rem .5rem .5rem',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    marginTop: '.75rem',
  },
  measurementLink: {
    padding: '.5rem 1rem .5rem .5rem',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    border: 0,
    marginTop: '.75rem',
  },
  measurementActionArea: {
    padding: '1.5rem 1rem',
    justifyContent: 'flex-start',
    gap: '1rem',
  },
  iconTitle: {
    paddingLeft: '5px',
    verticalAlign: 'top',
    lineHeight: '32px',
  },
  measurementViewTitle: {
    margin: '1rem 0 0 1rem',
    fontWeight: 700,
    fontSize: '40px',
  },
  measurementPickerTitle: {
    paddingLeft: '5px',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.link,
    verticalAlign: 'top',
    lineHeight: '32px',
  },
  measurementPickerIcon: {
    color: theme.palette.link,
    float: 'right',
    marginTop: '5px',
  },
  measurementSectionSubheading: {
    margin: '0 0 0 0',
    fontSize: '16px',
  },
  checkResultMessage: {
    fontSize: '16px',
    margin: 0,
    marginLeft: '32px',
  },
  accordionContent: {
    marginTop: '-32px',
    marginBottom: '-16px',
  },
  accordionNumberBadge: {
    verticalAlign: 'text-top',
    lineHeight: 0,
  },
  chipInformational: {
    marginLeft: '.25em',
    backgroundColor: theme.palette.infoText,
    marginBottom: '.25em',
  },
  chipError: {
    marginLeft: '.25em',
    backgroundColor: theme.palette.errorText,
    marginBottom: '.25em',
  },
  chipLabel: {
    color: 'white',
    fontWeight: 700,
    fontSize: '12px',
  },
  unstyledButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    outline: 'inherit',
    font: 'inherit',
    padding: 0,
    color: 'inherit',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:focus': {
      outline: `2px solid ${theme.palette.link}`,
      borderRadius: '2px',
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  unselected: {
    backgroundColor: theme.palette.background.default,
  },
  issueTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    tableLayout: 'fixed',
  },
  tableHeader: {
    textAlign: 'left',
    padding: '8px',
    borderBottom: '1px solid #ddd',
    fontWeight: 'bold',
    width: '50%',
  },
  tableCell: {
    padding: '8px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'middle',
    width: '50%',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
  },
  expandedRow: {
    // Smooth transition for expanded row
    opacity: 1,
    transform: 'translateY(0)',
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
}));

export interface AccordionProps {
  title: string;
  issues: Issue[];
}

export interface Issue {
  id?: string; // Issue ID for autofix lookup
  issueType: string;
  title?: string; // Issue title for Finding column
  dueDate: DueDate;
  severity?: string;
  focus?: string;
  issueDescription: string;
  issueDetails: string;
  remediationSteps: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any
  _meta?: any;
  updatedAt?: string;
  autoFix?: YardstickAutoFix;
  checkId?: string; // Check ID for analytics
  name?: string;
  domainId?: string;
  resourceID?: string; // Resource ID for resource-based issues
  resourceName?: string; // Resource name for resource-based issues
}

const markdownLinkRenderer = {
  a: (linkProps: any) => (
    <Link
      href={linkProps.href}
      component="a"
      color="primary"
      target="_blank"
      rel="noopener noreferrer"
    >
      {linkProps.children}
    </Link>
  ),
};

const IssueResultRow = (props: { issue: Issue }): ReactElement => {
  const classes = useStyles();
  const theme = useTheme();

  const { issueType, focus, issueDetails, remediationSteps } = props.issue;

  return (
    <>
      <div>
        <h4 style={{ fontSize: '16px', margin: '1rem 0 0 0' }}>
          {focus === 'ent-required' && (
            <ErrorOutline style={{ color: theme.palette.errorText }} />
          )}
          <span className={classes.iconTitle}>{issueType}</span>
        </h4>
        <div>
          <p className={classes.checkResultMessage}>
            <MarkdownContent content={issueDetails} dialect="gfm" />
          </p>
          <p className={classes.checkResultMessage}>
            <strong>Remediation Steps</strong>
          </p>
          <p className={classes.checkResultMessage}>
            <ReactMarkdown components={markdownLinkRenderer}>
              {remediationSteps}
            </ReactMarkdown>
          </p>
        </div>
      </div>
    </>
  );
};

export const InsightsAccordion = (props: AccordionProps): ReactElement => {
  const classes = useStyles();
  // const theme = useTheme();
  const themeConfig = useApi(appThemeApiRef);
  const activeTheme = themeConfig.getActiveThemeId();
  // eslint-disable-next-line no-undefined
  const useDarkTheme =
    activeTheme === 'principal-dark-theme' ||
    (activeTheme === undefined && prefersDarkMode);

  return (
    <PdsAccordion
      className={classes.measurementSectionSubheading}
      variant={useDarkTheme ? 'inverted' : 'default'}
    >
      <PdsHeading headingTag="h4" variant="label-default" slot="summary-title">
        {props.title}
        <Chip
          size="small"
          label={
            <Typography className={classes.chipLabel}>
              {props.issues.length}
            </Typography>
          }
          className={classes.chipError}
        />
      </PdsHeading>
      <span slot="accordion-content">
        <div className={classes.accordionContent}>
          {props.issues.map(issue => (
            <Fragment key={issue.issueType}>
              <IssueResultRow issue={issue} />
            </Fragment>
          ))}
        </div>
      </span>
    </PdsAccordion>
  );
};

export const LogicalAccordion = (props: AccordionProps): ReactElement => {
  const classes = useStyles();
  // const theme = useTheme();
  const themeConfig = useApi(appThemeApiRef);
  const activeTheme = themeConfig.getActiveThemeId();
  // eslint-disable-next-line no-undefined
  const useDarkTheme =
    activeTheme === 'principal-dark-theme' ||
    (activeTheme === undefined && prefersDarkMode);

  // Track which row is expanded (using issue key)
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);

  const featureFlagsApi = useApi(featureFlagsApiRef);
  const isFlagActive = (flagKey: string): boolean =>
    featureFlagsApi.isActive(flagKey);

  // Auto-remediation is offered whenever an issue has an autofix config, except
  // Endor SCA findings, which additionally require the SCA-remediation flag.
  // Gating here drives both the badge and the per-row button below.
  const canRemediate = (issue: Issue): boolean =>
    shouldShowRemediation(
      {
        hasAutoFix: Boolean(issue.autoFix || issue._meta?.autoFix),
        checkId: issue.checkId || issue._meta?.checkId,
      },
      isFlagActive,
    );

  const hasAnyAutofixConfig = props.issues.some(canRemediate);

  const issueAutofixMap = new Map<string, YardstickAutoFix>();
  props.issues.forEach(issue => {
    const autofixConfig = issue.autoFix || issue._meta?.autoFix;
    if (canRemediate(issue) && autofixConfig && (issue.id || issue.issueType)) {
      issueAutofixMap.set(issue.id || issue.issueType, autofixConfig);
    }
  });

  return (
    <PdsAccordion
      className={classes.measurementSectionSubheading}
      variant={useDarkTheme ? 'inverted' : 'default'}
      condensed
      style={{ paddingTop: '20px', paddingBottom: '20px' }}
    >
      <PdsHeading headingTag="h4" variant="label-default" slot="summary-title">
        <div
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: '8px' }}
        >
          <span>Missing or invalid values</span>
          <Chip
            size="small"
            label={
              <Typography className={classes.chipLabel}>
                {props.issues.length}
              </Typography>
            }
            className={classes.chipInformational}
          />
          {hasAnyAutofixConfig && (
            <span style={{ alignSelf: 'center' }}>
              <AutoRemediationBadge />
            </span>
          )}
        </div>
      </PdsHeading>
      <span slot="accordion-content">
        <div className={classes.accordionContent}>
          <table className={classes.issueTable}>
            <thead>
              <tr>
                <th
                  className={classes.tableHeader}
                  style={{ width: hasAnyAutofixConfig ? '30%' : '50%' }}
                >
                  Finding
                </th>
                <th
                  className={classes.tableHeader}
                  style={{ width: hasAnyAutofixConfig ? '30%' : '50%' }}
                >
                  Description
                </th>
                {hasAnyAutofixConfig && (
                  <th className={classes.tableHeader} style={{ width: '40%' }}>
                    {/* Unnamed column for autofix button */}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {props.issues.map((issue, index) => {
                const issueKey =
                  issue.id || issue.issueType || `issue-${index}`;
                const autofixConfig = issueAutofixMap.get(
                  issue.id || issue.issueType,
                );
                const isExpanded = expandedRowKey === issueKey;

                // When expanded, replace the row entirely with the AutofixV2LogicalAccordionComponent
                if (isExpanded && autofixConfig) {
                  return (
                    <tr key={issueKey}>
                      <td
                        colSpan={3}
                        className={`${classes.tableCell} ${classes.expandedRow}`}
                        style={{
                          padding: 0,
                          border: 'none',
                        }}
                      >
                        <AutofixV2LogicalAccordionComponent
                          issueId={issueKey}
                          issueTitle={issue.title || issue.issueType}
                          autofixConfig={autofixConfig}
                          remediationSteps={issue._meta?.remediationSteps}
                          entityRef={issue._meta?.entityRef}
                          checkId={issue.checkId || issue._meta?.checkId}
                          isExpanded={isExpanded}
                          onToggle={() => setExpandedRowKey(null)}
                        />
                      </td>
                    </tr>
                  );
                }

                // Normal row rendering when not expanded
                return (
                  <tr key={issueKey} style={{ position: 'relative' }}>
                    <td
                      className={classes.tableCell}
                      style={{ width: hasAnyAutofixConfig ? '30%' : '50%' }}
                    >
                      {issue.title || issue.issueType}
                    </td>
                    <td
                      className={classes.tableCell}
                      style={{ width: hasAnyAutofixConfig ? '30%' : '50%' }}
                    >
                      {issue.issueDescription ||
                      issue.issueDetails ||
                      (issue as any).message ? (
                        <ReactMarkdown components={markdownLinkRenderer}>
                          {issue.issueDescription ||
                            issue.issueDetails ||
                            (issue as any).message}
                        </ReactMarkdown>
                      ) : (
                        'No description available'
                      )}
                    </td>
                    {hasAnyAutofixConfig && (
                      <td
                        className={classes.tableCell}
                        style={{
                          width: '40%',
                          textAlign: 'right',
                          verticalAlign: 'top',
                          padding: '8px',
                          position: 'relative',
                        }}
                      >
                        {autofixConfig && (
                          <div
                            style={{
                              display: 'inline-block',
                              paddingRight: '8px',
                            }}
                          >
                            <AutoRemediationButton
                              isExpanded={false}
                              onClick={() => setExpandedRowKey(issueKey)}
                            />
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </span>
    </PdsAccordion>
  );
};

export const ResourceBasedAccordion = (props: AccordionProps): ReactElement => {
  const classes = useStyles();
  // const theme = useTheme();
  const themeConfig = useApi(appThemeApiRef);
  const activeTheme = themeConfig.getActiveThemeId();
  // eslint-disable-next-line no-undefined
  const useDarkTheme =
    activeTheme === 'principal-dark-theme' ||
    (activeTheme === undefined && prefersDarkMode);

  // Render the resource id as an AWS console link when a per-resource
  // `resourceUrl` is available; otherwise fall back to plain text. Keeps the
  // grouped table backward-compatible for findings that don't provide a url.
  const renderResourceId = (
    issue: (typeof props.issues)[number],
  ): ReactElement => {
    const value =
      issue._meta?.resourceArn ||
      issue._meta?.resourceId ||
      issue.resourceID ||
      'N/A';
    return issue._meta?.resourceUrl ? (
      <Link
        href={issue._meta.resourceUrl}
        component="a"
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        {value}
      </Link>
    ) : (
      <>{value}</>
    );
  };

  // Check if ALL issues have SCA-specific fields (packagePath, summary, links).
  // Using .every() ensures that mixed arrays (itext + non-itext) do not incorrectly
  // trigger SCA column rendering and show 'N/A' for non-itext findings.
  const hasPackagePath = props.issues.every(issue => issue._meta?.packagePath);
  const hasSummary = props.issues.every(issue => issue._meta?.summary);
  const hasLinks = props.issues.every(issue => issue._meta?.links);

  // Check if we have both resourceID and resourceName in any issues
  const hasResourceName = props.issues.some(
    issue => issue._meta?.resourceName || issue.resourceName,
  );
  const hasResourceID = props.issues.some(
    issue =>
      issue._meta?.resourceArn || issue._meta?.resourceId || issue.resourceID,
  );

  // Determine if this is an SCA finding with custom columns.
  // All issues must have SCA metadata to render SCA-specific columns.
  const isScaFinding = hasPackagePath && hasSummary && hasLinks;

  return (
    <PdsAccordion
      className={classes.measurementSectionSubheading}
      variant={useDarkTheme ? 'inverted' : 'default'}
      condensed
      style={{ paddingTop: '20px', paddingBottom: '20px' }}
    >
      <PdsHeading headingTag="h4" variant="label-default" slot="summary-title">
        {isScaFinding ? 'Affected packages' : 'Affected resources'}
        <Chip
          size="small"
          label={
            <Typography className={classes.chipLabel}>
              {props.issues.length}
            </Typography>
          }
          className={classes.chipInformational}
        />
      </PdsHeading>
      <span slot="accordion-content">
        <div className={classes.accordionContent}>
          <table className={classes.issueTable}>
            <thead>
              <tr>
                {isScaFinding ? (
                  <>
                    <th
                      className={classes.tableHeader}
                      style={{ width: '30%' }}
                    >
                      Package path
                    </th>
                    <th
                      className={classes.tableHeader}
                      style={{ width: '40%' }}
                    >
                      Summary
                    </th>
                    <th
                      className={classes.tableHeader}
                      style={{ width: '30%' }}
                    >
                      Links
                    </th>
                  </>
                ) : (
                  <>
                    {hasResourceName && hasResourceID && (
                      <>
                        <th className={classes.tableHeader}>Resource ID</th>
                        <th className={classes.tableHeader}>Resource Name</th>
                      </>
                    )}
                    {hasResourceID && !hasResourceName && (
                      <th
                        className={classes.tableHeader}
                        style={{ width: '100%' }}
                      >
                        Resource ID
                      </th>
                    )}
                    {!hasResourceID && hasResourceName && (
                      <th
                        className={classes.tableHeader}
                        style={{ width: '100%' }}
                      >
                        Resource Name
                      </th>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {props.issues.map((issue, index) => (
                <tr key={issue.id || issue.issueType || index}>
                  {isScaFinding ? (
                    <>
                      <td
                        className={classes.tableCell}
                        style={{ width: '30%' }}
                      >
                        {issue._meta?.packagePath || 'N/A'}
                      </td>
                      <td
                        className={classes.tableCell}
                        style={{ width: '40%' }}
                      >
                        {issue._meta?.summary || 'N/A'}
                      </td>
                      <td
                        className={classes.tableCell}
                        style={{ width: '30%' }}
                      >
                        <MarkdownContent
                          content={issue._meta?.links || 'N/A'}
                          dialect="gfm"
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      {hasResourceName && hasResourceID && (
                        <>
                          <td className={classes.tableCell}>
                            {renderResourceId(issue)}
                          </td>
                          <td className={classes.tableCell}>
                            {issue._meta?.resourceName ||
                              issue.resourceName ||
                              'N/A'}
                          </td>
                        </>
                      )}
                      {hasResourceID && !hasResourceName && (
                        <td
                          className={classes.tableCell}
                          style={{ width: '100%' }}
                        >
                          {renderResourceId(issue)}
                        </td>
                      )}
                      {!hasResourceID && hasResourceName && (
                        <td
                          className={classes.tableCell}
                          style={{ width: '100%' }}
                        >
                          {issue._meta?.resourceName ||
                            issue.resourceName ||
                            'N/A'}
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </span>
    </PdsAccordion>
  );
};
